import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  maintenanceClusterLinks,
  maintenanceKnowledgeBaseLinks,
  navbarCopyPl,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/maintenance-landing/shared";
import { faqsPl } from "@data/przejecie-projektu-landing/faq";
import {
  AlertTriangle,
  ArrowRightLeft,
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  Code,
  FileText,
  KeyRound,
  LifeBuoy,
  MessageSquareWarning,
  RefreshCw,
  Rocket,
  Search,
  Shield,
  Users,
  Wrench,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Przejęcie projektu od innego wykonawcy - bezpieczne wdrożenie i dalszy rozwój";

export const description =
  "Przejęcie projektu IT i zmiana dostawcy oprogramowania — bezpieczny handover, stabilizacja produkcji i dalszy rozwój. Polski software house wchodzi, gdy poprzedni vendor nie dowozi tempa ani jakości.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Przejęcie projektu od innego wykonawcy",
    description,
    "Software project takeover and vendor transition",
  ),
  faqStructuredData(faqsPl),
];

export const navbarCopy: NavbarCopy = {
  ...navbarCopyPl,
  cta: "Omów przejęcie",
};

export const heroCopy: HeroCopy = {
  header: "Przejęcie projektu",
  headerTail: "od innego wykonawcy",
  description:
    "Bezpiecznie zmieniamy dostawcę oprogramowania: odzyskujemy kontrolę nad kodem i produkcją, stabilizujemy release'y i wracamy do przewidywalnego tempa rozwoju — bez chaotycznego „od dziś jesteście na wszystkim”.",
  cta: {
    text: "Omów zmianę dostawcy",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Jak wygląda handover",
    href: "#proces",
  },
  benefits: [
    { text: "Bezpieczny handover dostępów", Icon: CheckCircle },
    { text: "Stabilizacja przed rozwojem", Icon: CheckCircle },
    { text: "Powrót do tempa delivery", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Kiedy warto zmienić dostawcę i przejąć projekt?",
    p: "To nie jest strona o budowie MVP ani o samym audycie. To dla firm, które mają działający produkt — i rosnącą frustrację: poprzedni vendor spóźnia sprinty, gubi kontekst albo nie reaguje na produkcję. Rozpoznajecie któryś scenariusz?",
  },
  points: [
    {
      Icon: Clock,
      title: "Poprzedni wykonawca nie dowozi tempa",
      description:
        "Deadline'y się rozjeżdżają, „mała zmiana” trwa tygodnie, komunikacja zamiast delivery. Chcecie partnera, który odzyska rytm release'ów.",
    },
    {
      Icon: MessageSquareWarning,
      title: "Jakość i przewidywalność poszły w dół",
      description:
        "Regresje na produkcji, brak testów, deploy „na czuja”. Potrzebujecie stabilizacji i jasnej odpowiedzialności za system.",
    },
    {
      Icon: ArrowRightLeft,
      title: "Chcecie zmienić software house bez przestoju biznesu",
      description:
        "Handover musi być kontrolowany: dostępy, sekrety, CI/CD, store, chmura — zanim nowy zespół weźmie dyżur.",
    },
    {
      Icon: Users,
      title: "Zespół in-house nie ogarnia całego spadku po vendorze",
      description:
        "Macie PM lub kilku developerów, ale brakuje mocy na utrzymanie + rozwój. Szukacie zewnętrznego zespołu do przejęcia toru produktowego.",
    },
    {
      Icon: RefreshCw,
      title: "Kontrakt z poprzednim dostawcą się kończy",
      description:
        "Macie okno na uporządkowany exit: lista dostępów, wiedza krytyczna, plan pierwszych 30 dni z nowym partnerem.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opiszcie produkt i co najbardziej boli u obecnego / poprzedniego wykonawcy — zaproponujemy plan przejęcia i pierwsze kroki.",
    href: "#kontakt",
    buttonText: "Umów rozmowę o przejęciu",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego osobna ścieżka na przejęcie projektu?",
    p: "Audyt i SLA odpowiadają na inne pytania. Tu trigger jest ludzki i operacyjny: zmiana dostawcy po problemach z delivery. Dlatego prowadzimy handover jako osobny proces — z naciskiem na kontrolę, tempo i spokój na produkcji.",
    imageAlt:
      "Przejęcie projektu od innego wykonawcy — bezpieczna zmiana dostawcy IT",
  },
  values: [
    {
      Icon: Shield,
      title: "Najpierw kontrola, potem obietnice funkcji",
      description:
        "Zanim obiecamy roadmapę, bierzemy odpowiedzialność za buildy, środowiska i krytyczne ścieżki. Bez tego „dalszy rozwój” to hazard.",
    },
    {
      Icon: KeyRound,
      title: "Handover dostępów bez luk",
      description:
        "Repo, CI/CD, cloud, domeny, store, monitoring, sekrety — checklista i śledzenie braków zamiast założenia, że „na pewno wszystko jest”.",
    },
    {
      Icon: Rocket,
      title: "Powrót do tempa, nie wieczny audyt",
      description:
        "Krótka ocena ryzyk na wejściu, potem delivery. Pełny audyt lub modernizację dokładamy tylko gdy naprawdę blokują rozwój.",
    },
    {
      Icon: Wrench,
      title: "Stabilizacja i rozwój w jednym zespole",
      description:
        "Ten sam zespół, który przejmuje projekt, może prowadzić SLA i sprinty — bez kolejnej zmiany partnera po handouverze.",
    },
    {
      Icon: Users,
      title: "Bezpośredni kontakt z inżynierami",
      description:
        "Rozmowa o stanie kodu i produkcji z ludźmi, którzy potem robią release — nie przez warstwę accountów.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Co obejmuje przejęcie projektu od innego wykonawcy",
    p: "Od inwentaryzacji dostępów po stabilizację i dalszy rozwój. Zakres dopasowujemy do tego, jak „gorący” jest exit z poprzednim vendorrem.",
  },
  services: [
    {
      Icon: ClipboardList,
      title: "Inwentaryzacja i plan handoveru",
      description:
        "Lista systemów, dostępów, środowisk i luk. Plan pierwszych 30 dni z jasnymi kamieniami milowymi.",
    },
    {
      Icon: KeyRound,
      title: "Przejęcie kontroli nad repo i wdrożeniami",
      description:
        "Git, CI/CD, sekrety, uprawnienia cloud/store — żebyście nie zależeli od skrzynki poprzedniego zespołu.",
    },
    {
      Icon: Search,
      title: "Szybka ocena ryzyk na wejściu",
      description:
        "Nie zawsze pełny audyt — wystarczy mapa tego, co może wybuchnąć w pierwszym miesiącu, i co blokuje tempo.",
    },
    {
      Icon: AlertTriangle,
      title: "Stabilizacja produkcji",
      description:
        "Krytyczne bugi, monitoring, procedury release i rollback — zanim wrócimy do feature'ów.",
    },
    {
      Icon: Code,
      title: "Dalszy rozwój produktu",
      description:
        "Backlog, sprinty, przewidywalne delivery po odzyskaniu kontroli nad systemem.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie ze SLA po przejęciu",
      description:
        "Dyżur, czasy reakcji i comiesięczny obraz zdrowia systemu — gdy produkcja ma działać spokojnie.",
    },
    {
      Icon: FileText,
      title: "Dokumentacja krytycznych ścieżek",
      description:
        "Runbooki deployu, integracji i eskalacji — żeby wiedza nie znikała z kolejnym odejściem osoby.",
    },
    {
      Icon: RefreshCw,
      title: "Opcjonalnie: audyt lub modernizacja",
      description:
        "Gdy po handouverze widać twardy dług techniczny — osobna ścieżka diagnozy i planu napraw, bez mieszania z samym przejęciem.",
    },
  ],
  cta: {
    h4: "Rozważacie zmianę software house?",
    p: "Napiszcie, na jakim etapie jesteście z poprzednim dostawcą — zaproponujemy bezpieczny plan przejęcia.",
    button: {
      text: "Umów rozmowę o przejęciu",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak przejmujemy projekt od innego wykonawcy",
    p: "Od rozmowy o problemach z vendorrem po odpowiedzialność za produkcję i rozwój. Etapy ułożone tak, żeby nie zgubić kontroli w trakcie zmiany.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Rozmowa o sytuacji i celach",
      duration: "w kilka godzin",
      description:
        "Co nie działa u obecnego dostawcy, jaki jest stan kontraktu, co musi działać bez przerwy, jaki budżet na wejście.",
    },
    {
      Icon: ClipboardList,
      title: "NDA i lista dostępów",
      duration: "w kilka dni",
      description:
        "Repo, CI, cloud, store, monitoring, domeny, dokumentacja. Mapujemy braki jeszcze przed startem prac.",
    },
    {
      Icon: Search,
      title: "Wejście techniczne",
      duration: "1–2 tygodnie",
      description:
        "Build lokalnie i na CI, przegląd środowisk, krytyczne ryzyka, propozycja planu stabilizacji.",
    },
    {
      Icon: KeyRound,
      title: "Przejęcie odpowiedzialności operacyjnej",
      duration: "ustalony moment cutover",
      description:
        "Jasna data: kto bierze dyżur, kto robi release, jak wygląda eskalacja — bez szarej strefy między vendorami.",
    },
    {
      Icon: Wrench,
      title: "Stabilizacja",
      duration: "2–4 tygodnie",
      description:
        "Gaszenie pożarów, porządkowanie deployu, minimum dokumentacji i monitoring pod dalszą pracę.",
    },
    {
      Icon: Rocket,
      title: "Powrót do rozwoju",
      duration: "wg backlogu",
      description:
        "Sprinty / retainer rozwojowy z przewidywalnym tempem — to zwykle główny powód zmiany dostawcy.",
    },
    {
      Icon: FileText,
      title: "Przegląd po 30–60 dniach",
      duration: "po starcie",
      description:
        "Co się poprawiło w delivery i stabilności, co wymaga audytu lub modernizacji, jak wygląda dalszy model współpracy.",
    },
    {
      Icon: CheckCircle,
      title: "Stała opieka lub rozwój produktu",
      duration: "na stałe",
      description:
        "SLA, roadmapa i kolejne iteracje — albo handover do Waszego rosnącego zespołu in-house.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów przejęcie projektu",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski software house do przejęcia projektów IT",
  p1: "Jesteśmy software house'em z Polski — przejmujemy projekty po innych wykonawcach, gdy tempo, jakość albo komunikacja przestają działać. Bezpieczny handover, stabilizacja produkcji i dalszy rozwój w jednym zespole.",
  p2: "Kuba ogarnia backend i infrastrukturę przy zmianie dostępów, Mateusz pilnuje UX i jakości release'ów, a zespół produktowy prowadzi Was przez cutover bez warstwy account managerów.",
  imageAlt:
    "Zespół Blues Brackets — przejęcie projektu od innego wykonawcy",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o przejęcie projektu i zmianę dostawcy",
  },
  faqs: faqsPl,
};

const takeoverContactForm: FooterCopy["contactForm"] = {
  ...contactFormPl,
  fields: {
    ...contactFormPl.fields,
    topic: {
      label: "Opis projektu / sytuacji z dostawcą",
      placeholder:
        "Krótko opisz produkt, stack oraz co nie działa u obecnego lub poprzedniego wykonawcy (tempo, jakość, komunikacja, dostępność)...",
      requiredError: "Opisz swój projekt",
      maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
    },
  },
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcecie zmienić dostawcę bez chaosu na produkcji?",
    p: "Umówcie rozmowę — opiszcie sytuację z poprzednim wykonawcą, a zaproponujemy plan bezpiecznego przejęcia.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów rozmowę o przejęciu",
    p: "Przejęcie projektu IT i zmiana dostawcy oprogramowania — bezpieczny handover, stabilizacja i dalszy rozwój. Polski software house.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/utrzymanie-i-audyt-oprogramowania",
        text: "Utrzymanie i audyt oprogramowania",
      },
      ...maintenanceClusterLinks.filter(
        l =>
          l.href !== "/utrzymanie-i-audyt-oprogramowania" &&
          l.href !== "/przejecie-projektu-od-innego-wykonawcy",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...maintenanceKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: takeoverContactForm,
};
